import { createSelector } from 'reselect';
import { getLocDisplayOption } from './ui';
import { getPointTypes, getTagsLabelsForPointType } from './pointtypes';
import { parseStringToInt } from '../../utils/parsing';
import {
  compareByNumericProp,
  compareEquipTypes,
  comparePointTypes,
} from '../../utils/comparators';
import { getFloor } from '../constants/floors';

// an 'input-selector' function that returns all equipment
// in redux store for a given building id
const getEquipAndPointsByBuilding = (state, bid) =>
  (state.entities.buildings.equipment[bid]?.ids || []).map((equipId) =>
    getEquipment(state, equipId)
  );

const addChildEquip = (equip, state) => {
  const childEquip = [];
  for (let i = 0; i < equip.length; i += 1) {
    const equipment = equip[i];
    for (let j = 0; j < equipment.children.length; j += 1) {
      const childEquipId = equipment.children[j];
      childEquip.push(getEquipment(state, childEquipId));
    }
  }
  return equip.concat(childEquip);
};

export const getFilteredEquipmentForBuilding = (state, params) => {
  const { inclChild, hideChild, bid } = params;
  const floorNumPhysFilter = parseStringToInt(params.floorPhys);
  const floorNumServedFilter = parseStringToInt(params.floorServed);
  const etid = parseStringToInt(params.etid);
  const equipData = getEquipAndPointsByBuilding(state, bid);
  const equipFilteredByFloorPhys = floorNumPhysFilter
    ? equipData.filter((equip) => equip.floorNumPhys === floorNumPhysFilter)
    : equipData;
  const equipFilteredByFloorServed = floorNumServedFilter
    ? equipData.filter((equip) => equip.floorNumServed === floorNumServedFilter)
    : equipFilteredByFloorPhys;
  const equipFilteredByType = etid
    ? equipFilteredByFloorServed.filter((equip) => equip.typeId === etid)
    : equipFilteredByFloorServed;
  const equipFilteredChildren = hideChild
    ? equipFilteredByType.filter(
        (equip) =>
          (equip.parents && equip.parents.length === 0) ||
          equip.typeAbbr === 'VAV' ||
          equip.typeAbbr === 'CAV'
      )
    : equipFilteredByType;
  return inclChild === 'true' ? addChildEquip(equipFilteredChildren, state) : equipFilteredChildren;
};

// a 'memoized selector' created by using 'createSelector' from reselect.
// It takes getEquipAndPointsByBuilding as an input selector and a transform
// function that 'calculates' the data to return (work in this case is done in
// the above function)
export const getEquipmentForBuilding = createSelector(
  [getEquipAndPointsByBuilding],
  (equipAndPointsByBuilding) => equipAndPointsByBuilding
);

const reduceEquipToEquipTypes = (equipAndPointsByBuilding) => {
  // map list of equipment to hashamp of equipmenttype
  const equipTypes = equipAndPointsByBuilding.reduce((hashMap, equip) => {
    // retrieve the equip type from the hash map else create a new one
    // eslint-disable-next-line no-prototype-builtins
    const equipType = hashMap.hasOwnProperty(equip.typeName)
      ? hashMap[equip.typeName]
      : {
          nameLong: equip.typeName,
          nameAbbr: equip.typeAbbr,
          flowOrder: equip.flowOrder,
          instances: [],
        };
    equipType.instances.push(equip);
    // eslint-disable-next-line no-param-reassign
    hashMap[equip.typeName] = equipType;
    return hashMap;
  }, {});
  const toSort = Array.from(Object.values(equipTypes));
  toSort.sort(compareEquipTypes);
  return toSort;
};

export const getEquipmentByFloor = createSelector(
  [getFilteredEquipmentForBuilding, getLocDisplayOption],
  (equipAndPointsByBuilding, locationOption) => {
    // organize all equipment by floor
    const byFloor = equipAndPointsByBuilding.reduce((floorGroups, equip) => {
      const floor =
        locationOption === 'areaserved'
          ? getFloor(equip.floorNumServed)
          : getFloor(equip.floorNumPhys);
      if (!(floor.name in floorGroups)) {
        // eslint-disable-next-line no-param-reassign
        floorGroups[floor.name] = {
          floorNum: floor.floorNum,
          name: floor.name,
          sortOrder: floor.sortOrder,
          equipment: [],
        };
      }
      floorGroups[floor.name].equipment.push(equip);
      return floorGroups;
    }, {});
    return byFloor;
  }
);

export const getEquipmentByFloorAndPointType = createSelector([getEquipmentByFloor], (byFloor) => {
  // for each floor, group equipment by type
  const equipByFloorAndType = [];
  const floorNames = Object.getOwnPropertyNames(byFloor);
  for (let i = 0; i < floorNames.length; i += 1) {
    const floorName = floorNames[i];
    equipByFloorAndType.push({
      floorNum: byFloor[floorName].floorNum,
      name: floorName,
      sortOrder: byFloor[floorName].sortOrder,
      equipTypes: reduceEquipToEquipTypes(byFloor[floorName].equipment),
    });
  }
  equipByFloorAndType.sort(compareByNumericProp('sortOrder', 'desc'));
  return equipByFloorAndType;
});

function getCountByEquipType(equipData) {
  const counts = {};
  for (let i = 0; i < equipData.length; i += 1) {
    counts[equipData[i].typeName] = 1 + (counts[equipData[i].typeName] || 0);
  }
  const summary = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const key in counts) {
    // eslint-disable-next-line no-prototype-builtins
    if (counts.hasOwnProperty(key)) {
      const row = {};
      row.equip = key;
      row.count = counts[key];
      summary.push(row);
    }
  }
  summary.sort(compareByNumericProp('count', 'desc'));
  return summary;
}

export const getEquipmentSummaryForBuilding = createSelector(
  [getEquipAndPointsByBuilding],
  (equipData) => getCountByEquipType(equipData)
);

export const getEquipmentByEquipTypeForBuilding = createSelector(
  [getFilteredEquipmentForBuilding],
  (equipAndPointsByBuilding) => reduceEquipToEquipTypes(equipAndPointsByBuilding)
);

const groupPointsByType = (equipment, pointTypeLookup) => {
  const pointTypes = {};
  for (let i = 0; i < equipment.length; i += 1) {
    const equip = equipment[i];
    for (let j = 0; j < equip.points?.length; j += 1) {
      const point = equip.points[j];
      const pointTypeName =
        pointTypeLookup && point.pointTypeId in pointTypeLookup
          ? pointTypeLookup[point.pointTypeId].name
          : 'Unknown';
      if (!(point.pointTypeId in pointTypes)) {
        pointTypes[point.pointTypeId] = {
          pointTypeId: point.pointTypeId,
          pointTypeName,
          points: [],
        };
      }
      pointTypes[point.pointTypeId].points.push({
        pointId: point.id,
        equipId: equip.id,
        equipName: equip.display.fullName,
        location: getFloor(equip.floorNumPhys).name,
        areaServed: getFloor(equip.floorNumServed).name,
        ...point,
      });
    }
  }
  const toSort = Array.from(Object.values(pointTypes));
  toSort.sort(comparePointTypes);
  return toSort;
};

export const getPointsByType = createSelector(
  [getFilteredEquipmentForBuilding, getPointTypes],
  (equipment, pointTypeLookup) => groupPointsByType(equipment, pointTypeLookup)
);

export const getPointsByFloorAndType = createSelector(
  [getEquipmentByFloor, getPointTypes],
  (equipByFloor, pointTypeLookup) => {
    const floorNames = Object.getOwnPropertyNames(equipByFloor);
    for (let i = 0; i < floorNames.length; i += 1) {
      const { equipment } = equipByFloor[floorNames[i]];
      const pointsByType = groupPointsByType(equipment, pointTypeLookup);
      // eslint-disable-next-line no-param-reassign
      equipByFloor[floorNames[i]].pointsByType = pointsByType;
    }
    return equipByFloor;
  }
);

export const getEquipCountForSelectedBuilding = createSelector(
  [getFilteredEquipmentForBuilding],
  (equipData) => equipData.length
);

/**
 * This function adds fields to a bare equipment object from the store in order
 * to prepare it for display by components
 */
export function getEquipment(state, id) {
  const equipment = { id, ...state.entities.equipment.byId[id] };

  // join with the points store
  equipment.points = [];
  for (let j = 0; j < equipment.pointIds?.length; j += 1) {
    const pointId = equipment.pointIds[j];
    const point = state.entities.points.byId[pointId];
    if (point) equipment.points.push(point);
  }

  // let's also include the tags & labels for the points
  for (let i = 0; i < equipment.points.length; i += 1) {
    const pointWithTags = equipment.points[i];
    const tagsLabels = getTagsLabelsForPointType(state, pointWithTags.pointTypeId);
    pointWithTags.tags = tagsLabels.tags;
    pointWithTags.labels = tagsLabels.labels;
  }

  // and a display object with fields resolved from other stores
  if (equipment.subTypeId) {
    const subtype = state.entities.equipSubTypes.byId[equipment.subTypeId];
    if (subtype) {
      equipment.display = {
        fullName:
          equipment.suffix !== null ? `${subtype.nameAbbr} ${equipment.suffix}` : subtype.nameAbbr,
        typeName: subtype.nameLong,
      };
    }
    equipment.labels = subtype?.labels;
  } else {
    const type = state.entities.equipTypes.byId[equipment.typeId];
    equipment.labels = type?.labels;
  }
  // make sure we populate something if e.g. the subtypes haven't loaded yet
  if (!equipment.display) {
    equipment.display = {
      fullName:
        equipment.suffix !== null
          ? `${equipment.typeAbbr} ${equipment.suffix}`
          : equipment.typeAbbr,
      typeName: equipment.typeName,
    };
  }

  if (!equipment.tags) {
    equipment.tags = [];
  }
  return equipment;
}

export function getSelectedEquipmentWithDetails(state, urlQueryString) {
  const urlParams = new URLSearchParams(urlQueryString);
  const equipId = parseStringToInt(urlParams.get('eid'));
  if (!equipId) return null;
  const equipment = getEquipment(state, equipId);
  const building = state.entities.buildings.byId[equipment.buildingId];
  return {
    ...equipment,
    physLocation: equipment && getFloor(equipment.floorNumPhys).name,
    buildingName: building?.name,
  };
}

export default getEquipmentForBuilding;
